import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';
import { callParkingBoxApi } from '../lib/parkingBoxApi';
import { normalizeCityListResponse, type ParkingCityItem } from '../lib/parkingCities';
import { getParkingData } from '../lib/parkingDataSource';
import { clearStoredSelectedCityId, persistSelectedCityId, readStoredSelectedCityId } from '../lib/selectedCity';

type CitySelectionContextValue = {
  cities: ParkingCityItem[];
  isCitiesLoading: boolean;
  selectedCityId: string;
  selectedCityName: string | null;
  setSelectedCityId: (cityId: string) => void;
};

const CitySelectionContext = createContext<CitySelectionContextValue | undefined>(undefined);

type CitySelectionProviderProps = {
  children: ReactNode;
};

const parkingData = getParkingData();
const fallbackCities: ParkingCityItem[] = parkingData.cities.map((city) => ({
  id: city.id,
  name: city.name,
}));
const fallbackDefaultCityId = parkingData.defaultCityId;

export function CitySelectionProvider({ children }: CitySelectionProviderProps) {
  const { token, isAuthenticated } = useAuth();
  const [cities, setCities] = useState<ParkingCityItem[]>(fallbackCities);
  const [isCitiesLoading, setIsCitiesLoading] = useState(false);
  const [selectedCityId, setSelectedCityIdState] = useState<string>(() => {
    const storedCityId = readStoredSelectedCityId();
    if (storedCityId) {
      return storedCityId;
    }
    if (fallbackDefaultCityId && fallbackCities.some((city) => city.id === fallbackDefaultCityId)) {
      return fallbackDefaultCityId;
    }
    return fallbackCities[0]?.id ?? '';
  });

  const setSelectedCityId = useCallback((cityId: string) => {
    setSelectedCityIdState(cityId.trim());
  }, []);

  useEffect(() => {
    if (!selectedCityId) {
      clearStoredSelectedCityId();
      return;
    }

    persistSelectedCityId(selectedCityId);
  }, [selectedCityId]);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setCities(fallbackCities);
      setIsCitiesLoading(false);
      return;
    }

    let isCancelled = false;

    const fetchCities = async () => {
      setIsCitiesLoading(true);
      try {
        const response = await callParkingBoxApi(
          {
            action: 'get-cities',
          },
          {
            jwtToken: token,
            useStoredToken: true,
          },
        );

        if (isCancelled) {
          return;
        }

        const parsedCities = normalizeCityListResponse(response.json);
        setCities(parsedCities.length > 0 ? parsedCities : fallbackCities);
      } catch (error) {
        if (!isCancelled) {
          console.error('[CitySelectionProvider] Nie udało się pobrać listy miast:', error);
          setCities(fallbackCities);
        }
      } finally {
        if (!isCancelled) {
          setIsCitiesLoading(false);
        }
      }
    };

    void fetchCities();

    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated, token]);

  useEffect(() => {
    if (cities.length === 0) {
      return;
    }

    setSelectedCityIdState((currentCityId) => {
      if (currentCityId && cities.some((city) => city.id === currentCityId)) {
        return currentCityId;
      }

      const storedCityId = readStoredSelectedCityId();
      if (storedCityId && cities.some((city) => city.id === storedCityId)) {
        return storedCityId;
      }

      if (fallbackDefaultCityId && cities.some((city) => city.id === fallbackDefaultCityId)) {
        return fallbackDefaultCityId;
      }

      return cities[0]?.id ?? '';
    });
  }, [cities]);

  const selectedCityName = useMemo(
    () => cities.find((city) => city.id === selectedCityId)?.name ?? null,
    [cities, selectedCityId],
  );

  const value = useMemo<CitySelectionContextValue>(
    () => ({
      cities,
      isCitiesLoading,
      selectedCityId,
      selectedCityName,
      setSelectedCityId,
    }),
    [cities, isCitiesLoading, selectedCityId, selectedCityName, setSelectedCityId],
  );

  return <CitySelectionContext.Provider value={value}>{children}</CitySelectionContext.Provider>;
}

export function useCitySelectionContext() {
  const context = useContext(CitySelectionContext);
  if (!context) {
    throw new Error('useCitySelectionContext must be used within <CitySelectionProvider />');
  }
  return context;
}
